import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Users,
  Truck,
  AlertCircle,
  Save,
  Loader2,
  ChevronRight,
  CalendarClock
} from 'lucide-react';
import { WS_STATUS_CONFIG, WS_STATUS_ORDER, canSetReschedule } from '@/config/statusWorkflow';

const EditWorkSchedulePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, currentFacility, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Data states
  const [workSchedule, setWorkSchedule] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  
  // Form states
  const [formData, setFormData] = useState({
    schedule_start_date: '',
    schedule_end_date: '',
    priority: 'MEDIUM',
    complexity: 'STANDARD',
    notes: '',
    status: '',
    job_lead_id: '',
    support_team_ids: [],
    vehicle_ids: [],
    vehicle_not_needed: false,
    needs_specialist_review: false
  });

  // Must come AFTER formData declaration
  const supportTeamOptions = useMemo(
    () => technicians.filter(t => t.id !== formData.job_lead_id),
    [technicians, formData.job_lead_id]
  );

  const fetchWorkSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/work-schedules/${id}`);
      const ws = response.data;
      setWorkSchedule(ws);
      
      // Populate form data
      setFormData({
        schedule_start_date: ws.schedule_start_date || ws.schedule_date || '',
        schedule_end_date: ws.schedule_end_date || '',
        priority: ws.priority || 'MEDIUM',
        complexity: ws.complexity || 'STANDARD',
        notes: ws.notes || '',
        status: ws.status || 'SCHEDULED',
        job_lead_id: ws.job_lead_id || '',
        support_team_ids: ws.support_team_ids || [],
        vehicle_ids: ws.vehicle_ids || [],
        vehicle_not_needed: ws.vehicle_not_needed || false,
        needs_specialist_review: ws.needs_specialist_review || false
      });
    } catch (error) {
      console.error('Failed to fetch work schedule:', error);
      toast.error('Failed to load work schedule');
      navigate('/work-schedules');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  const fetchTechnicians = useCallback(async () => {
    try {
      const response = await api.get('/users', { params: { role: 'TECHNICIAN' } });
      setTechnicians(response.data);
    } catch (error) {
      console.error('Failed to fetch technicians:', error);
    }
  }, [api]);

  const fetchVehicles = useCallback(async () => {
    try {
      const params = {};
      if (currentFacility && currentFacility !== 'all') {
        params.facility_id = currentFacility;
      }
      const response = await api.get('/vehicles', { params });
      setVehicles(response.data.filter(v => v.status === 'ACTIVE'));
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  }, [api, currentFacility]);

  useEffect(() => {
    if (id) {
      fetchWorkSchedule();
      fetchTechnicians();
      fetchVehicles();
    }
  }, [id, fetchWorkSchedule, fetchTechnicians, fetchVehicles]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSupportTeamToggle = (techId) => {
    setFormData(prev => {
      const current = prev.support_team_ids || [];
      if (current.includes(techId)) {
        return { ...prev, support_team_ids: current.filter(id => id !== techId) };
      } else {
        return { ...prev, support_team_ids: [...current, techId] };
      }
    });
  };

  const handleVehicleToggle = (vehicleId) => {
    setFormData(prev => {
      const current = prev.vehicle_ids || [];
      if (current.includes(vehicleId)) {
        return { ...prev, vehicle_ids: current.filter(id => id !== vehicleId) };
      } else {
        return { ...prev, vehicle_ids: [...current, vehicleId] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.schedule_start_date) {
      toast.error('Please select a schedule start date');
      return;
    }
    if (!formData.job_lead_id) {
      toast.error('Please select a job lead');
      return;
    }
    // 2026-07-05 Wave 2 #3: Truck validation applies to both types on edit too.
    const hasVehicle = (formData.vehicle_ids || []).length > 0;
    if (!hasVehicle && !formData.vehicle_not_needed) {
      toast.error('Select a truck or check "Vehicle not needed" to continue.');
      return;
    }
    
    const isDedicated = workSchedule?.schedule_type === 'DEDICATED_SITE';
    setSubmitting(true);
    try {
      const payload = {
        schedule_start_date: formData.schedule_start_date,
        schedule_end_date: formData.schedule_end_date || formData.schedule_start_date,
        notes: formData.notes || null,
        job_lead_id: formData.job_lead_id,
        support_team_ids: formData.support_team_ids,
        vehicle_ids: formData.vehicle_not_needed ? [] : formData.vehicle_ids,
        vehicle_not_needed: formData.vehicle_not_needed
      };
      // Wave 2 #5: Dedicated Site edit must NOT expose Priority / Complexity /
      // Specialist Review — omit them from the payload so backend values stay put.
      if (!isDedicated) {
        payload.priority = formData.priority;
        payload.complexity = formData.complexity;
        payload.needs_specialist_review = formData.needs_specialist_review;
      }
      
      // Include status change if it's different
      if (formData.status !== workSchedule.status) {
        payload.status = formData.status;
      }
      
      await api.put(`/work-schedules/${id}`, payload);
      
      if (formData.status === 'RESCHEDULE' && formData.status !== workSchedule.status) {
        toast.success('Work order updated and marked for rescheduling');
      } else {
        toast.success('Work schedule updated successfully');
      }
      
      navigate(`/work-schedules/${id}`);
    } catch (error) {
      console.error('Failed to update work schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to update work schedule');
    } finally {
      setSubmitting(false);
    }
  };

  // Get available status options - include RESCHEDULE if allowed
  const getStatusOptions = () => {
    if (!workSchedule) return [];
    
    const currentStatus = workSchedule.status;
    const options = [];
    
    // Always include current status
    options.push(currentStatus);
    
    // Add adjacent statuses in the linear chain
    const currentIdx = WS_STATUS_ORDER.indexOf(currentStatus);
    if (currentIdx > 0) {
      options.push(WS_STATUS_ORDER[currentIdx - 1]);
    }
    if (currentIdx >= 0 && currentIdx < WS_STATUS_ORDER.length - 1) {
      options.push(WS_STATUS_ORDER[currentIdx + 1]);
    }
    
    // Add RESCHEDULE if allowed
    if (canSetReschedule(currentStatus) && !options.includes('RESCHEDULE')) {
      options.push('RESCHEDULE');
    }
    
    // Special case: RESCHEDULE can go back to SCHEDULED
    if (currentStatus === 'RESCHEDULE') {
      options.push('SCHEDULED');
    }
    
    // Remove duplicates and return
    return [...new Set(options)];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8]"></div>
      </div>
    );
  }

  if (!workSchedule) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#323033] mb-2">Work Schedule Not Found</h2>
        <Button onClick={() => navigate('/work-schedules')}>Back to List</Button>
      </div>
    );
  }

  // FIX 1B: Lockdown for Closed/Cancelled - redirect to detail page
  const isFinalStatus = workSchedule.status === 'CLOSED' || workSchedule.status === 'CANCELLED';
  if (isFinalStatus) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="edit-work-schedule-locked">
        {/* Lockdown Banner */}
        <div className={`p-4 rounded-lg ${workSchedule.status === 'CANCELLED' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
          <div className="flex items-start gap-3">
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${workSchedule.status === 'CANCELLED' ? 'text-red-600' : 'text-green-600'}`} />
            <div>
              <p className={`font-medium ${workSchedule.status === 'CANCELLED' ? 'text-red-800' : 'text-green-800'}`}>
                {workSchedule.status === 'CANCELLED' 
                  ? 'This work order has been cancelled and cannot be edited.'
                  : 'This work order is closed and cannot be edited.'
                }
              </p>
              <p className={`text-sm mt-1 ${workSchedule.status === 'CANCELLED' ? 'text-red-700' : 'text-green-700'}`}>
                View the work order details to see the current state.
              </p>
            </div>
          </div>
        </div>
        
        <Button onClick={() => navigate(`/work-schedules/${id}`)} className="bg-[#005EF8] hover:bg-[#0047C2] text-white">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Work Order
        </Button>
      </div>
    );
  }

  const sr = workSchedule.service_request;
  const statusOptions = getStatusOptions();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="edit-work-schedule-page">
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 text-sm text-[#5A5A5A]">
        <Link to="/work-schedules" className="hover:text-[#005EF8] transition-colors">
          Work Schedules
        </Link>
        <ChevronRight className="w-4 h-4" />
        <Link to={`/work-schedules/${id}`} className="hover:text-[#005EF8] transition-colors">
          {sr?.job_number || 'Details'}
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-[#323033] font-medium">Edit</span>
      </nav>

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            // 2026-07-06 — Back arrow always returns to the WS list to avoid
            // routing DedSite users through the SR-detail page which 500s.
            navigate('/work-schedules');
          }}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">Edit Work Schedule</h1>
          {/* 2026-07-06 — Only render the subtitle when there is actual
              content; DedSite records have no SR and used to render just " • ". */}
          {(sr?.car_mark || sr?.car_number || sr?.job_number) && (
            <p className="text-[#5A5A5A] mt-1">
              {[sr?.car_mark, sr?.car_number].filter(Boolean).join(' ')}
              {(sr?.car_mark || sr?.car_number) && sr?.job_number ? ' • ' : ''}
              {sr?.job_number || ''}
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Schedule Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#005EF8]" />
              Schedule Details
            </CardTitle>
            <CardDescription>
              Update the schedule dates and job details. To reschedule, change the status to "Reschedule" and update the dates.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="schedule_start_date">Schedule Start Date *</Label>
                <Input
                  type="date"
                  id="schedule_start_date"
                  name="schedule_start_date"
                  value={formData.schedule_start_date}
                  onChange={handleInputChange}
                  required
                  data-testid="schedule-start-date-input"
                />
              </div>
              <div>
                <Label htmlFor="schedule_end_date">Schedule End Date</Label>
                <Input
                  type="date"
                  id="schedule_end_date"
                  name="schedule_end_date"
                  value={formData.schedule_end_date}
                  onChange={handleInputChange}
                  min={formData.schedule_start_date}
                  data-testid="schedule-end-date-input"
                />
                <p className="text-xs text-[#5A5A5A] mt-1">Leave blank for single-day jobs</p>
              </div>
              {/* Wave 2 #5: Priority + Complexity hidden on Dedicated Site edit. */}
              {workSchedule?.schedule_type !== 'DEDICATED_SITE' && (
                <>
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={formData.priority} onValueChange={(v) => handleSelectChange('priority', v)}>
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LOW">Low</SelectItem>
                        <SelectItem value="MEDIUM">Medium</SelectItem>
                        <SelectItem value="HIGH">High</SelectItem>
                        <SelectItem value="CRITICAL">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="complexity">Complexity *</Label>
                    <Select value={formData.complexity} onValueChange={(v) => handleSelectChange('complexity', v)}>
                      <SelectTrigger data-testid="complexity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LIGHT">Light</SelectItem>
                        <SelectItem value="STANDARD">Standard</SelectItem>
                        <SelectItem value="HEAVY">Heavy</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            {/* Specialist Review Flag — hidden for Dedicated Site (Wave 2 #5). */}
            {workSchedule?.schedule_type !== 'DEDICATED_SITE' && (
            <div className="flex items-center space-x-3 py-2">
              <Checkbox
                id="needs_specialist_review"
                checked={formData.needs_specialist_review}
                onCheckedChange={(checked) => handleSelectChange('needs_specialist_review', checked)}
                data-testid="needs-specialist-review-checkbox"
              />
              <Label 
                htmlFor="needs_specialist_review" 
                className="text-sm font-medium cursor-pointer flex items-center gap-2"
              >
                Needs specialist review
                <span className="text-xs text-[#808080] font-normal">(flags this schedule for special attention)</span>
              </Label>
            </div>
            )}

            {/* Status Change */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <CalendarClock className="w-5 h-5 text-[#005EF8]" />
                <Label className="text-base font-medium">Status</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status">Work Order Status</Label>
                  <Select value={formData.status} onValueChange={(v) => handleSelectChange('status', v)}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((status) => {
                        const config = WS_STATUS_CONFIG[status] || { label: status };
                        return (
                          <SelectItem key={status} value={status}>
                            <div className="flex items-center gap-2">
                              <span>{config.label}</span>
                              {status === 'RESCHEDULE' && (
                                <span className="text-xs text-amber-600">(Update dates below)</span>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {formData.status === 'RESCHEDULE' && formData.status !== workSchedule.status && (
                    <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Update the schedule dates above for the rescheduled job
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes about this work schedule..."
                rows={3}
                data-testid="notes-textarea"
              />
            </div>
          </CardContent>
        </Card>

        {/* Crew Assignment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#005EF8]" />
              Crew Assignment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="job_lead">Job Lead *</Label>
              <Select value={formData.job_lead_id} onValueChange={(v) => handleSelectChange('job_lead_id', v)}>
                <SelectTrigger data-testid="job-lead-select">
                  <SelectValue placeholder="Select job lead..." />
                </SelectTrigger>
                <SelectContent>
                  {technicians.map((tech) => (
                    <SelectItem key={tech.id} value={tech.id}>
                      {tech.first_name} {tech.last_name} {tech.technician_id ? `(${tech.technician_id})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Support Team (Optional)</Label>
              <p className="text-sm text-[#5A5A5A] mb-2">Select additional technicians to assist</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {supportTeamOptions.map((tech) => (
                    <div key={tech.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`support-${tech.id}`}
                        checked={(formData.support_team_ids || []).includes(tech.id)}
                        onCheckedChange={() => handleSupportTeamToggle(tech.id)}
                        data-testid={`support-tech-${tech.id}`}
                      />
                      <label
                        htmlFor={`support-${tech.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {tech.first_name} {tech.last_name}
                      </label>
                    </div>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Assignment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#005EF8]" />
              Vehicle Assignment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vehicle_not_needed"
                checked={formData.vehicle_not_needed}
                onCheckedChange={(checked) => setFormData(prev => ({ 
                  ...prev, 
                  vehicle_not_needed: checked,
                  vehicle_ids: checked ? [] : prev.vehicle_ids
                }))}
                data-testid="vehicle-not-needed"
              />
              <label htmlFor="vehicle_not_needed" className="text-sm cursor-pointer">
                Vehicle not needed for this job
              </label>
            </div>

            {!formData.vehicle_not_needed && (
              <div>
                <Label>Select Vehicles</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                  {vehicles.length === 0 ? (
                    <p className="text-sm text-[#5A5A5A] col-span-full">No active vehicles available.</p>
                  ) : (
                    vehicles.map((vehicle) => {
                      const isSelected = (formData.vehicle_ids || []).includes(vehicle.id);
                      return (
                        <div
                          key={vehicle.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-[#005EF8] bg-[#005EF8]/5'
                              : 'border-border hover:border-[#005EF8]/50'
                          }`}
                          onClick={() => handleVehicleToggle(vehicle.id)}
                          data-testid={`vehicle-${vehicle.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <div 
                              className={`w-4 h-4 rounded border flex items-center justify-center ${
                                isSelected 
                                  ? 'bg-[#005EF8] border-[#005EF8]' 
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{vehicle.display_id}</p>
                              <p className="text-xs text-[#5A5A5A]">
                                {vehicle.year} {vehicle.make} {vehicle.model}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              // 2026-07-06 — Dedicated Site cancel bounces to the WS list
              // (its detail route lives at /work-schedules/dedicated/:id and
              // the SR-style detail route 500s for DedSite records).
              if (workSchedule?.schedule_type === 'DEDICATED_SITE') {
                navigate('/work-schedules');
              } else {
                navigate(`/work-schedules/${id}`);
              }
            }}
            data-testid="edit-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-[#005EF8] hover:bg-[#0047C2] text-white"
            disabled={submitting}
            data-testid="submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditWorkSchedulePage;
